/**
 * DaVinci API Pricing Data Extractor
 *
 * Extracts pricing information from a DaVinci BA (Booking Action) response and
 * turns it into the data the checkout order summary renders.
 *
 * Model (see BA-Cleaning spec):
 * - The line total of a SalesPrices entry is `Price × Amount` (Price is the unit
 *   price, Amount the multiplier).
 * - Each H (accommodation) service splits into:
 *     • room price   = Σ(Price×Amount) of components with SeparateOnDocuments=false
 *                      (board-as-component, "Kind" / "Teilnehmer" child and
 *                      extra-person lines are naturally absorbed),
 *     • separate items = components with SeparateOnDocuments=true ("Einzelausweis"
 *                      in DaVinci), collapsed by Text per room and shown on their own
 *                      priced line (e.g. "Endreinigung", "Frühbucher-Vorteil").
 * - A collapsed separate item with a negative amount is a rebate (VAM-304). DaVinci
 *   splits rebates per participant (and per seasonal price line), so collapsing by
 *   Text is what turns them into one line per room.
 * - Board (meal) is shown by name only, never with a price.
 * - Every non-H service is classified by Requirement/Service (see {@link classifyService}):
 *     PRK            → package marker, skipped (no price),
 *     SO + NO_* /    → "no childcare" marker, skipped,
 *       name "Keine…"
 *     otherwise      → auxiliary service (childcare AN, service fee SO/SERVICE,
 *                      …) listed with a price, or "incl." when it has none.
 * - `TotalSalesPrice` already sums every component of every service (incl.
 *   separate items), so it is the deviation base and the main-column total base.
 */

import type { ExtractedDiscount } from '@/types/schemas/checkout/discount';

/**
 * SalesPrices components flagged `SeparateOnDocuments=true` with the same Text,
 * collapsed into one priced line below the room (e.g. "Endreinigung 80 €",
 * "Frühbucher-Vorteil -464,10 €"). `amount` keeps its sign.
 */
export interface SeparateItem {
  /** Component label, from SalesPrices[].Text. */
  label: string;
  /** Σ(Price × Amount) of the collapsed components, rounded to cents (may be negative). */
  amount: number;
  /** True when the collapsed amount is negative, i.e. the item is a rebate. */
  isRebate: boolean;
  /** Rebate percentage (0-100) from PercentInput, the largest of the collapsed lines. */
  percent?: number;
}

/** A rebate summed across all rooms by its label, for the payment-step price table. */
export interface RebateSummaryLine {
  /** Rebate name, from SalesPrices[].Text (e.g. "Frühbucher-Vorteil"). */
  label: string;
  /** Summed rebate, rounded to cents. Always < 0. */
  amount: number;
}

/**
 * A non-H service shown below room & board (childcare, service fee, …).
 * `included` is true when the service has no price → render "incl." instead of "0 €".
 */
export interface AuxService {
  /** Display name: BookingCodeInfo.Name → SalesPrices[0].Text → Service code. */
  label: string;
  /** Service price (Σ Price×Amount or Foreign); 0 when none. */
  price: number;
  /** True when there is no price → show the "incl." translation. */
  included: boolean;
}

/**
 * Extracted pricing data from a DaVinci BA response.
 */
export interface ExtractedPricing {
  /**
   * Per accommodation (Requirement 'H') service price by 1-based Service.Index.
   * Value is `PriceInfo.SalesPrice.Foreign` (room + board + separate items).
   */
  servicePrices: Map<number, number>;

  /** Total price for all services, from `Booking.TotalSalesPrice`. */
  totalPrice: number;

  /**
   * Order-summary box total (rooms + board + separate items + aux services),
   * i.e. `TotalSalesPrice`. Insurance/extras (other APIs) are added on top of
   * this in the main column, not here.
   */
  accommodationTotal: number;

  /**
   * Optional per-participant pricing (1-based participant IDs).
   * Key: participant index (1, 2, 3…). Value: Participant.Price.
   */
  participantPrices?: Map<number, number>;

  /**
   * Room price per H service (1-based Index): `Σ(Price×Amount, SEP=false)`.
   * Rebates are separate items, so this is the room price before the rebate.
   * Legacy negative SEP=false lines (old DaVinci setup) stay absorbed here and are
   * not surfaced as a rebate.
   */
  roomOnlyPrices: Map<number, number>;

  /**
   * Board (meal) name per H service (1-based Index), from
   * `MealPlanCodeDetails.Description`. Shown without a price. Absent when the
   * service carries no meal description.
   */
  boardNames: Map<number, string>;

  /**
   * Board (meal) description per H service (1-based Index): the meal part of
   * `InfoText1` when it says more than the board name (e.g. "Mittag- oder
   * Abendessen" for "Abendessen"). Absent when it adds nothing.
   */
  boardDescriptions: Map<number, string>;

  /**
   * Separate-on-documents line items per H service (1-based Index), collapsed by
   * Text and each shown on its own priced line below the room, in DaVinci order
   * (e.g. "Endreinigung", "Frühbucher-Vorteil").
   */
  separateItems: Map<number, SeparateItem[]>;

  /**
   * Auxiliary services (every service that is not H, not PRK and not a NO_*
   * marker): childcare (AN), service fee (SO/SERVICE), … Listed below room &
   * board with a price, or "incl." when they have none.
   */
  auxServices: AuxService[];

  /**
   * Rebates: the separate items with a negative amount, one entry per room and
   * rebate name, in room order.
   */
  discounts: ExtractedDiscount[];

  /**
   * Rebates summed across all rooms by name, in order of first appearance. Shown
   * as rows in the payment-step price table.
   */
  rebateSummary: RebateSummaryLine[];

  /** Net rebate: sum of the {@link discounts}, rounded to cents. Always ≤ 0. */
  totalDiscount: number;

  /**
   * Accommodation price before the rebate (`accommodationTotal − totalDiscount`).
   * Since `totalDiscount ≤ 0`, this is ≥ `accommodationTotal`. Used for the
   * struck-through "original price".
   */
  originalPrice: number;

  /**
   * Badge percentage (0-100): the largest PercentInput of the rebates. Undefined
   * when no rebate carries one (e.g. "7 Tage reisen - 6 Tage bezahlen") — it is not
   * derived from the prices, because rebates do not apply to every component
   * (e.g. breakfast), which would understate the advertised percentage.
   */
  discountPercent?: number;
}

/** Line total for a SalesPrices entry: Price × Amount (Amount defaults to 1). */
function getLineTotal(entry: Record<string, unknown>): number {
  const price = entry.Price;
  if (typeof price !== 'number') return 0;
  const amount = entry.Amount;
  const multiplier = typeof amount === 'number' ? amount : 1;
  return price * multiplier;
}

/** A component shown separately on the documents (`SeparateOnDocuments === true`). */
function isSeparateLine(entry: Record<string, unknown>): boolean {
  return entry.SeparateOnDocuments === true;
}

/** Round a currency amount to cents, removing floating-point noise from sums. */
function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Normalize PercentInput from the DaVinci response. A rebate carries a negative
 * percentage (e.g. -15 for 15%); this returns its absolute value. Anything outside
 * [-100, 0) is not a usable rebate percentage — older setups returned e.g. `100`
 * on a Stay/Pay line — and yields undefined.
 */
function normalizePercentInput(value: unknown): number | undefined {
  if (typeof value !== 'number' || isNaN(value)) return undefined;
  if (value >= 0 || value < -100) return undefined;
  return Math.abs(value);
}

/** The larger of two optional percentages. */
function maxPercent(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.max(a, b);
}

/** Read `PriceInfo.SalesPrice.Foreign` as a number, or undefined. */
function getForeign(service: Record<string, unknown>): number | undefined {
  const priceInfo = service.PriceInfo as Record<string, unknown> | undefined;
  const salesPrice = priceInfo?.SalesPrice as Record<string, unknown> | undefined;
  const foreign = salesPrice?.Foreign;
  return typeof foreign === 'number' ? foreign : undefined;
}

/** Board (meal) name for a service, from MealPlanCodeDetails.Description. */
function getBoardName(service: Record<string, unknown>): string | undefined {
  const meal = service.MealPlanCodeDetails as Record<string, unknown> | undefined;
  const description = meal?.Description;
  if (typeof description === 'string' && description.trim() !== '') {
    return description.trim();
  }
  return undefined;
}

/** Comparison form of a label: NFC, collapsed whitespace, lower case. */
function normalizeLabel(value: string): string {
  return value.normalize('NFC').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Whether `candidate` is the part of `infoText` after a comma separator. */
function isTrailingPart(infoText: string, candidate: string): boolean {
  const text = infoText.trimEnd();
  if (!text.endsWith(candidate)) return false;
  return text.slice(0, -candidate.length).trimEnd().endsWith(',');
}

/**
 * Board (meal) description for a service.
 *
 * DaVinci has no dedicated board-description field. `InfoText1` reads
 * "<room text>,<board text>", and its board part is the only board text richer
 * than the generic `MealPlanCodeDetails.Description` (probed on production, see
 * claude/specs/RoomDescriptionCheckoutSummary/FINDINGS.md).
 *
 * Rather than splitting `InfoText1` at a comma (either part may contain one, and
 * the field may be truncated), the board part must match a `SalesPrices[].Text`
 * line exactly: the board's own price line carries the same text. Returned only
 * when it differs from the board name, so an unchanged name never renders twice.
 */
export function getBoardDescription(
  service: Record<string, unknown>,
  boardName: string | undefined
): string | undefined {
  const infoText = service.InfoText1;
  const salesPrices = service.SalesPrices;
  if (!boardName || typeof infoText !== 'string' || !Array.isArray(salesPrices)) {
    return undefined;
  }
  const normalizedName = normalizeLabel(boardName);
  for (const line of salesPrices as Array<Record<string, unknown>>) {
    const text = typeof line?.Text === 'string' ? line.Text.trim() : '';
    if (text === '' || normalizeLabel(text) === normalizedName) continue;
    if (isTrailingPart(infoText, text)) return text;
  }
  return undefined;
}

type ServiceKind = 'accommodation' | 'skip' | 'auxiliary';

/**
 * Classify a service for the order summary:
 * - `accommodation` — Requirement 'H' (room & board, separate items),
 * - `skip` — PRK package marker, or a SO/NO_* "no childcare" marker (also matched
 *   defensively when BookingCodeInfo.Name starts with "Keine"),
 * - `auxiliary` — everything else (childcare AN, service fee SO/SERVICE, …).
 */
function classifyService(service: Record<string, unknown>): ServiceKind {
  const requirement = service.Requirement;

  if (requirement === 'H') return 'accommodation';

  // PRK is only a package-travel marker — no price, not listed.
  if (requirement === 'PRK') return 'skip';

  // Negative "no childcare" marker: SO + Service "NO_*" (or name "Keine …").
  if (requirement === 'SO') {
    const serviceCode = service.Service;
    if (typeof serviceCode === 'string' && serviceCode.startsWith('NO_')) return 'skip';

    const bookingCodeInfo = service.BookingCodeInfo as Record<string, unknown> | undefined;
    const name = bookingCodeInfo?.Name;
    if (typeof name === 'string' && name.trim().toLowerCase().startsWith('keine')) return 'skip';
  }

  return 'auxiliary';
}

/** Display label for an auxiliary service: BookingCodeInfo.Name → Text → Service. */
function getAuxLabel(service: Record<string, unknown>): string {
  const bookingCodeInfo = service.BookingCodeInfo as Record<string, unknown> | undefined;
  const name = bookingCodeInfo?.Name;
  if (typeof name === 'string' && name.trim() !== '') return name.trim();

  const salesPrices = service.SalesPrices;
  if (Array.isArray(salesPrices) && salesPrices.length > 0) {
    const text = (salesPrices[0] as Record<string, unknown>).Text;
    if (typeof text === 'string' && text.trim() !== '') return text.trim();
  }

  const serviceCode = service.Service;
  return typeof serviceCode === 'string' ? serviceCode : '';
}

/** Price for an auxiliary service: Foreign, else Σ(Price×Amount), else 0. */
function getAuxPrice(service: Record<string, unknown>): number {
  const foreign = getForeign(service);
  if (typeof foreign === 'number') return foreign;

  const salesPrices = service.SalesPrices;
  if (Array.isArray(salesPrices)) {
    return salesPrices.reduce(
      (sum: number, sp) =>
        sum +
        (typeof sp === 'object' && sp !== null ? getLineTotal(sp as Record<string, unknown>) : 0),
      0
    );
  }
  return 0;
}

/** A separate item while its components are being collapsed. */
interface SeparateAccumulator {
  label: string;
  amount: number;
  percent?: number;
}

/**
 * Split a single H service into room price and separate items.
 *
 * room      = Σ(Price×Amount, SEP=false)
 * separate  = SEP=true components collapsed by Text (first-appearance order), each
 *             summed with its sign; a negative sum is a rebate. Components without
 *             a Text are kept as their own items; items that net to 0 € (e.g. a
 *             rebate and its correction) are dropped.
 */
function splitAccommodationService(service: Record<string, unknown>): {
  roomPrice: number;
  separateItems: SeparateItem[];
} {
  const collapsed = new Map<string, SeparateAccumulator>();
  const order: SeparateAccumulator[] = [];
  let roomPrice = 0;
  let sawSalesPrices = false;

  const salesPrices = service.SalesPrices;
  if (Array.isArray(salesPrices)) {
    salesPrices.forEach((sp: unknown) => {
      if (typeof sp !== 'object' || sp === null) return;
      const entry = sp as Record<string, unknown>;
      sawSalesPrices = true;

      const lineTotal = getLineTotal(entry);

      if (!isSeparateLine(entry)) {
        roomPrice += lineTotal;
        return;
      }

      const label = typeof entry.Text === 'string' ? entry.Text.trim() : '';
      const percent = normalizePercentInput(entry.PercentInput);
      const existing = label !== '' ? collapsed.get(label) : undefined;

      if (existing) {
        existing.amount += lineTotal;
        existing.percent = maxPercent(existing.percent, percent);
        return;
      }

      const item: SeparateAccumulator = { label, amount: lineTotal, percent };
      if (label !== '') collapsed.set(label, item);
      order.push(item);
    });
  }

  if (!sawSalesPrices) {
    roomPrice = getForeign(service) ?? 0;
  }

  const separateItems = order
    .map((item): SeparateItem => {
      const amount = roundCents(item.amount);
      const isRebate = amount < 0;
      const separateItem: SeparateItem = { label: item.label, amount, isRebate };
      if (isRebate && item.percent !== undefined) separateItem.percent = item.percent;
      return separateItem;
    })
    .filter((item) => item.amount !== 0);

  return { roomPrice, separateItems };
}

/** A rebate separate item as a discount entry. */
function toDiscount(item: SeparateItem): ExtractedDiscount {
  const discount: ExtractedDiscount = { text: item.label, amount: item.amount };
  if (item.percent !== undefined) discount.percent = item.percent;
  return discount;
}

/** Sum rebates across rooms by name, keeping the order of first appearance. */
function summarizeRebates(discounts: readonly ExtractedDiscount[]): RebateSummaryLine[] {
  const byLabel = new Map<string, RebateSummaryLine>();
  discounts.forEach((discount) => {
    const line = byLabel.get(discount.text);
    if (line) line.amount = roundCents(line.amount + discount.amount);
    else byLabel.set(discount.text, { label: discount.text, amount: discount.amount });
  });
  return Array.from(byLabel.values());
}

/**
 * Extract pricing data from a DaVinci BA (Booking Action) response.
 *
 * @param bookingResponse - The `Booking` object from a DaVinci BA response.
 * @returns Extracted pricing data for the checkout order summary.
 */
export function extractPricingFromBookingResponse(
  bookingResponse: Record<string, unknown>
): ExtractedPricing {
  const result: ExtractedPricing = {
    servicePrices: new Map(),
    totalPrice: 0,
    accommodationTotal: 0,
    roomOnlyPrices: new Map(),
    boardNames: new Map(),
    boardDescriptions: new Map(),
    separateItems: new Map(),
    auxServices: [],
    discounts: [],
    rebateSummary: [],
    totalDiscount: 0,
    originalPrice: 0,
  };

  const services = bookingResponse.Services as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(services)) {
    services.forEach((service: Record<string, unknown>) => {
      const kind = classifyService(service);

      if (kind === 'skip') return;

      if (kind === 'auxiliary') {
        const price = getAuxPrice(service);
        result.auxServices.push({
          label: getAuxLabel(service),
          price,
          included: price <= 0,
        });
        return;
      }

      // Accommodation (Requirement 'H').
      const index = service.Index;
      if (typeof index !== 'number') return;

      const foreign = getForeign(service);
      if (typeof foreign === 'number') {
        result.servicePrices.set(index, foreign);
      }

      const boardName = getBoardName(service);
      if (boardName) result.boardNames.set(index, boardName);
      const boardDescription = getBoardDescription(service, boardName);
      if (boardDescription) result.boardDescriptions.set(index, boardDescription);

      const { roomPrice, separateItems } = splitAccommodationService(service);

      result.roomOnlyPrices.set(index, roomPrice);
      if (separateItems.length > 0) result.separateItems.set(index, separateItems);

      result.discounts.push(...separateItems.filter((item) => item.isRebate).map(toDiscount));
    });
  }

  const totalSalesPrice = bookingResponse.TotalSalesPrice;
  if (typeof totalSalesPrice === 'number') {
    result.totalPrice = totalSalesPrice;
  }

  // The order-summary box lists every BA service (rooms, separate items, aux),
  // so its total equals TotalSalesPrice. Insurance/extras are added on top in
  // the main column, not here.
  result.accommodationTotal = result.totalPrice;

  result.rebateSummary = summarizeRebates(result.discounts);
  result.totalDiscount = roundCents(
    result.discounts.reduce((sum, discount) => sum + discount.amount, 0)
  );
  result.originalPrice = roundCents(result.accommodationTotal - result.totalDiscount);
  result.discountPercent = result.discounts.reduce<number | undefined>(
    (max, discount) => maxPercent(max, discount.percent),
    undefined
  );

  const participants = bookingResponse.Participants as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(participants) && participants.length > 0) {
    const participantPrices = new Map<number, number>();
    participants.forEach((participant: Record<string, unknown>, i: number) => {
      const price = participant.Price;
      if (typeof price === 'number') participantPrices.set(i + 1, price); // 1-based IDs
    });
    if (participantPrices.size > 0) result.participantPrices = participantPrices;
  }

  return result;
}

/**
 * Check if a booking response contains valid pricing data.
 *
 * @param bookingResponse - The `Booking` object from a DaVinci BA response.
 * @returns true if at least one service has a numeric Foreign price.
 */
export function hasPricingData(bookingResponse: Record<string, unknown>): boolean {
  const services = bookingResponse.Services;
  if (!Array.isArray(services)) return false;

  return services.some((service: unknown) => {
    if (typeof service !== 'object' || service === null) return false;
    return getForeign(service as Record<string, unknown>) !== undefined;
  });
}

/**
 * Type guard to validate the PriceInfo structure.
 *
 * @param priceInfo - Unknown value to validate.
 * @returns true if priceInfo has a valid SalesPrice.Foreign structure.
 */
export function isValidPriceInfo(
  priceInfo: unknown
): priceInfo is { SalesPrice: { Foreign: number } } {
  if (typeof priceInfo !== 'object' || priceInfo === null) return false;
  const p = priceInfo as Record<string, unknown>;
  const salesPrice = p.SalesPrice;
  if (typeof salesPrice !== 'object' || salesPrice === null) return false;
  return typeof (salesPrice as Record<string, unknown>).Foreign === 'number';
}

/**
 * Validate that the displayed breakdown reconciles with the total price:
 * `Σ rooms + Σ separate items + Σ auxiliary services === TotalSalesPrice`.
 * This is exactly what the order summary renders, so it holds even when a service
 * has no Foreign and the room falls back to its component sum. Useful for
 * debugging; allows a 1-cent floating point difference.
 *
 * @param extracted - Extracted pricing data to validate.
 * @returns true if the breakdown reconciles with the total (within tolerance).
 */
export function validatePricingConsistency(extracted: ExtractedPricing): boolean {
  const roomSum = Array.from(extracted.roomOnlyPrices.values()).reduce((acc, p) => acc + p, 0);
  const separateSum = Array.from(extracted.separateItems.values())
    .flat()
    .reduce((acc, item) => acc + item.amount, 0);
  const auxSum = extracted.auxServices.reduce((acc, aux) => acc + aux.price, 0);

  const diff = Math.abs(roomSum + separateSum + auxSum - extracted.totalPrice);

  if (diff > 0.01) {
    console.warn('[DaVinci Pricing] Inconsistency detected:', {
      roomSum,
      separateSum,
      auxSum,
      totalPrice: extracted.totalPrice,
      difference: diff,
    });
    return false;
  }

  return true;
}
